import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useMemo } from 'react';

interface NewFeatureState {
  dismissed: boolean;
  shownCount: number;
}

type NewFeaturesStorage = Record<string, NewFeatureState>;

const storage = createJSONStorage<NewFeaturesStorage>(() => localStorage);

const newFeatureAtom = atomWithStorage<NewFeaturesStorage>('new-feature-badge', {}, storage);

const MAX_SHOWS = 3;

export const useShowNewFeatureBadge = (featureName: string) => {
  const showAtom = useMemo(
    () =>
      atom((get) => {
        const features = get(newFeatureAtom);
        const feature = features[featureName];

        if (!feature) return true;
        if (feature.dismissed) return false;
        if (feature.shownCount >= MAX_SHOWS) return false;

        return true;
      }),
    [featureName],
  );

  const showNewFeatureBadge = useAtomValue(showAtom);
  return showNewFeatureBadge;
};

export const useDismissNewFeatureBadge = (featureName: string) => {
  const dismissAtom = atom(null, (_get, set) => {
    set(newFeatureAtom, (prev) => ({
      ...prev,
      [featureName]: {
        dismissed: (prev[featureName]?.shownCount ?? 0) < MAX_SHOWS ? true : false,
        shownCount: prev[featureName]?.shownCount ?? 0,
      },
    }));
  });

  const dismissNewFeatureBadge = useSetAtom(dismissAtom);
  return dismissNewFeatureBadge;
};

export const useUpdateNewFeatureBadgeState = () => {
  const resetDismissedOnLogoutAtom = atom(null, (_get, set) => {
    set(newFeatureAtom, (prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([key, value]) => [
          key,
          {
            dismissed: false,
            shownCount: value.dismissed && value.shownCount < MAX_SHOWS ? value.shownCount + 1 : value.shownCount,
          },
        ]),
      ),
    );
  });

  const updateNewFeatureBadgeState = useSetAtom(resetDismissedOnLogoutAtom);
  return updateNewFeatureBadgeState;
};
